import React, { useEffect, useState } from 'react'
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, RadialBarChart, RadialBar, PolarAngleAxis, LineChart, Line } from 'recharts'
import styled, { createGlobalStyle } from 'styled-components'
import { API_URL } from '../../util'
import { SectionTitle, SectionTitleSize, SectionTitleStyle } from '../SectionTitle/SectionTitle'

// Interfaces for the dashboard data (should match backend)
interface KpiCategoryStats {
  onTarget: number;
  nearingTarget: number;
  belowTarget: number;
}

interface KpiCertificationStats {
  onTargetPercent: number;
  nearingTargetPercent: number;
  belowTargetPercent: number;
}

interface DashboardKpiData {
  overallAchievement: KpiCategoryStats;
  customerSatisfaction: KpiCategoryStats;
  certification: KpiCertificationStats;
}

// Extended interfaces for new functionality
interface MetricValue {
  current: number;
  lastMonth: number;
  trend12Months: number[];
  periods: string[];
}

interface ContractorKpiData {
  contractorId: string;
  contractorName: string;
  contractorAcronym?: string;
  categories: KpiCategoryStats;
  satisfactionPercentage?: number;
  complaintsCount?: number;
  progressPercentage?: number;
}

interface ContractorTimelinessData {
  contractorId: string;
  contractorName: string;
  contractorAcronym?: string;
  averageDaysLate: number;
  submissionCount: number;
}

interface ExtendedDashboardData extends DashboardKpiData {
  extended?: {
    satisfactionPercentage?: MetricValue;
    complaintsCount?: MetricValue;
    progressAgainstPlan?: {
      overall: number;
      byContractor: { [id: string]: number };
    };
    specificKpis?: {
      // Keyed by the selector sent to the API: question text for current
      // configs, numeric index (as a string) for legacy ones (#41).
      [key: string]: {
        label: string;
        value: number;
        trend: number[];
      };
    };
    contractorBreakdown?: ContractorKpiData[];
    submissionTimeliness?: ContractorTimelinessData[];
    dateRange: { from: string; to: string };
    templateId?: string;
    templateName?: string;
  };
}

const ChartContainer = styled.div`
  margin: 0;
  padding: 24px;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`

const ChartWrapper = styled.div<{ height: number; $sideBySide?: boolean }>`
  display: flex;
  justify-content: center;
  height: auto;
  min-width: 500px;
  width: 100%;
  margin: 0;
  padding: 0;
  ${({ $sideBySide }) => $sideBySide && `
    flex: 1 1 0;
    max-width: 1100px;
  `}
  @media (max-width: 1100px) {
    order: 1;
  }
`

const TableContainer = styled.div<{ $sideBySide?: boolean }>`
  margin: 16px 0 0 0;
  width: 100%;
  ${({ $sideBySide }) => $sideBySide && `
    flex: 1 1 0;
    min-width: 500px;
    max-width: 900px;
  `}
  @media (max-width: 1100px) {
    order: 2;
  }
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 1em;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
  margin-bottom: 16px;
  & th, & td {
    border: none;
    padding: 14px 16px;
    text-align: left;
    vertical-align: middle;
    font-size: 1em;
    transition: background 0.2s;
  }
  & th {
    background-color: #f3f6fa;
    font-weight: bold;
    color: #1a2233;
    letter-spacing: 0.02em;
    border-bottom: 2px solid #e3e8ee;
  }
  & tr:nth-child(even) td {
    background: #f8fafc;
  }
  & tr:hover td {
    background: #eaf3fb;
  }
`

const DEFAULT_COLORS = {
  onTarget: '#4CAF50',
  nearingTarget: '#FF9800',
  belowTarget: '#F44336',
}


// Remove black border/focus from chart container
const NoOutlineWrapper = styled.div`
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
`

// Remove all focus/active outlines, borders, and strokes from recharts SVG elements and children
const NoChartOutline = createGlobalStyle`
  .recharts-wrapper:focus, .recharts-wrapper:active,
  .recharts-wrapper *:focus, .recharts-wrapper *:active, .recharts-wrapper *:target,
  .recharts-surface:focus, .recharts-surface:active,
  .recharts-responsive-container:focus, .recharts-responsive-container:active,
  svg:focus, svg:active, svg:target,
  .recharts-wrapper rect:focus, .recharts-wrapper rect:active, .recharts-wrapper rect:target,
  .recharts-wrapper g:focus, .recharts-wrapper g:active, .recharts-wrapper g:target,
  .recharts-wrapper path:focus, .recharts-wrapper path:active, .recharts-wrapper path:target,
  .recharts-wrapper circle:focus, .recharts-wrapper circle:active, .recharts-wrapper circle:target {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
    stroke: none !important;
  }
`

const SideBySideContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  align-items: center;
  width: 100%;
  @media (max-width: 1100px) {
    flex-direction: column;
    gap: 0;
  }
`

const KpiFontGlobal = createGlobalStyle`
  .recharts-wrapper, .recharts-text, .recharts-legend-item-text, .recharts-tooltip-label, .recharts-tooltip-item, .recharts-default-legend {
    font-family: 'Montserrat', sans-serif !important;
  }
  table, th, td {
    font-family: 'Montserrat', sans-serif !important;
  }
`

type KpiChartProps = {
  chartType: 'overallAchievement' | 'customerSatisfaction' | 'certification' |
             'satisfactionPercentage' | 'complaintsCount' | 'progressAgainstPlan' |
             'specificKpis' | 'contractorComparison' | 'trendAnalysis' |
             'submissionTimeliness'
  title?: string
  showTitle?: boolean
  showTable?: boolean
  height?: number
  tablePosition?: 'below' | 'left' | 'right' | 'none'
  chartDisplayType?: 'pie' | 'doughnut' | 'bar' | 'polar' | 'line'
  showLegend?: boolean
  showAxisLabels?: boolean
  useAcronyms?: boolean
  // Extended parameters for new chart types
  templateId?: string
  templateIds?: string[]
  contractorId?: string
  dateFrom?: string
  dateTo?: string
  metrics?: string[]
  kpiIndices?: number[]
  // #41: current block configs select KPIs by question text (+ occurrence for
  // duplicates); index is the legacy stored format, still accepted by the API.
  // Values are passed through to the backend verbatim.
  selectedQuestions?: Array<{question?: string; occurrence?: number; index?: number; customName?: string}>
  groupBy?: 'overall' | 'contractor' | 'template'
  mode?: 'categories' | 'values' | 'both'
  // Custom colors
  colorOnTarget?: string
  colorNearingTarget?: string
  colorBelowTarget?: string
}

const chartTypeLabels = {
  overallAchievement: 'Achievement of All Targets',
  customerSatisfaction: 'Customer Satisfaction',
  certification: 'Certification',
  satisfactionPercentage: 'Customer Satisfaction Percentage',
  complaintsCount: 'Formal Complaints',
  progressAgainstPlan: 'Progress Against Plan',
  specificKpis: 'Specific KPI Metrics',
  contractorComparison: 'Contractor Comparison',
  trendAnalysis: 'KPI Trend Analysis',
  submissionTimeliness: 'Submission Days Late by Contractor',
}

export const KpiChart: React.FC<KpiChartProps> = ({
  chartType,
  title,
  showTitle = false,
  showTable = false,
  height = 420,
  tablePosition: tablePositionProp = 'below',
  chartDisplayType: chartDisplayTypeProp = 'pie',
  showLegend = false,
  showAxisLabels = false,
  useAcronyms = true,
  templateId,
  templateIds,
  contractorId,
  dateFrom,
  dateTo,
  metrics,
  kpiIndices,
  selectedQuestions,
  groupBy,
  mode,
  colorOnTarget,
  colorNearingTarget,
  colorBelowTarget,
}) => {
  const [data, setData] = useState<ExtendedDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const tablePosition = tablePositionProp || 'below'
  const chartDisplayType = chartDisplayTypeProp || 'pie'

  // Build COLORS dynamically from props with fallback to defaults
  const COLORS = {
    onTarget: colorOnTarget || DEFAULT_COLORS.onTarget,
    nearingTarget: colorNearingTarget || DEFAULT_COLORS.nearingTarget,
    belowTarget: colorBelowTarget || DEFAULT_COLORS.belowTarget,
  }

  // Determine if this is an extended chart type
  const isExtendedChart = [
    'satisfactionPercentage', 'complaintsCount', 'progressAgainstPlan',
    'specificKpis', 'contractorComparison', 'trendAnalysis',
    'submissionTimeliness'
  ].includes(chartType);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        // Build API URL with parameters for extended charts
        let apiUrl = `${API_URL}kpiDashboard`;
        if (isExtendedChart) {
          const params = new URLSearchParams();
          params.append('extended', 'true');
          params.append('chartType', chartType); // Tell API which specific chart data we need
          params.append('mode', mode || 'values');
          // Support multi-template: prefer templateIds, fall back to templateId
          const effectiveTemplateIds = templateIds || (templateId ? [templateId] : [])
          if (effectiveTemplateIds.length > 0) {
            params.append('templateIds', effectiveTemplateIds.join(','));
            params.append('templateId', effectiveTemplateIds[0]);
          }
          if (contractorId) params.append('contractorId', contractorId);
          if (dateFrom) params.append('dateFrom', dateFrom);
          if (dateTo) params.append('dateTo', dateTo);
          if (metrics) params.append('metrics', metrics.join(','));
          if (kpiIndices) params.append('kpiIndices', kpiIndices.join(','));
          if (selectedQuestions) params.append('selectedQuestions', JSON.stringify(selectedQuestions));
          // Auto-set groupBy for contractor-based charts
          if (chartType === 'contractorComparison' || chartType === 'submissionTimeliness') {
            params.append('groupBy', 'contractor');
          } else if (groupBy) {
            params.append('groupBy', groupBy);
          }

          apiUrl += `?${params.toString()}`;
        }

        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch KPI data')
        }
        const dashboardData = await response.json()
        setData(dashboardData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [chartType, templateId, templateIds, contractorId, dateFrom, dateTo, metrics, kpiIndices, selectedQuestions, groupBy, mode, isExtendedChart])

  if (loading) {
    return (
      <ChartContainer>
        {showTitle && (
          <SectionTitle title={title || chartTypeLabels[chartType]} size={SectionTitleSize.Medium} style={SectionTitleStyle.Dark} />
        )}
        <ChartWrapper height={height}>
          <p>Loading KPI data...</p>
        </ChartWrapper>
      </ChartContainer>
    )
  }

  if (error || !data) {
    return (
      <ChartContainer>
        {showTitle && (
          <SectionTitle title={title || chartTypeLabels[chartType]} size={SectionTitleSize.Medium} style={SectionTitleStyle.Dark} />
        )}
        <ChartWrapper height={height}>
          <p>Error loading KPI data: {error}</p>
        </ChartWrapper>
      </ChartContainer>
    )
  }

  // Transform the data for the selected chart type
  let chartData: { name: string; value: number; color?: string; period?: string; fullName?: string; acronym?: string }[]
  let isPercentage = false

  switch (chartType) {
    case 'overallAchievement':
      chartData = [
        { name: 'On Target', value: data.overallAchievement.onTarget, color: COLORS.onTarget },
        { name: 'Nearing Target', value: data.overallAchievement.nearingTarget, color: COLORS.nearingTarget },
        { name: 'Below Target', value: data.overallAchievement.belowTarget, color: COLORS.belowTarget },
      ]
      break
    case 'customerSatisfaction':
      chartData = [
        { name: 'On Target', value: data.customerSatisfaction.onTarget, color: COLORS.onTarget },
        { name: 'Nearing Target', value: data.customerSatisfaction.nearingTarget, color: COLORS.nearingTarget },
        { name: 'Below Target', value: data.customerSatisfaction.belowTarget, color: COLORS.belowTarget },
      ]
      break
    case 'certification':
      chartData = [
        { name: 'On Target', value: data.certification.onTargetPercent, color: COLORS.onTarget },
        { name: 'Nearing Target', value: data.certification.nearingTargetPercent, color: COLORS.nearingTarget },
        { name: 'Below Target', value: data.certification.belowTargetPercent, color: COLORS.belowTarget },
      ]
      isPercentage = true
      break
    case 'satisfactionPercentage':
      if (data.extended?.satisfactionPercentage) {
        // Use full trend data to show all months
        const satData = data.extended.satisfactionPercentage;
        chartData = satData.periods.map((period, index) => ({
          name: period,
          value: satData.trend12Months[index] || 0,
          color: COLORS.onTarget,
          period
        }));
        isPercentage = true
      } else {
        chartData = []
      }
      break
    case 'complaintsCount':
      if (data.extended?.complaintsCount) {
        // Use full trend data to show all months
        const compData = data.extended.complaintsCount;
        chartData = compData.periods.map((period, index) => ({
          name: period,
          value: compData.trend12Months[index] || 0,
          color: COLORS.belowTarget,
          period
        }));
      } else {
        chartData = []
      }
      break
    case 'progressAgainstPlan':
      if (data.extended?.progressAgainstPlan) {
        chartData = [
          { name: 'Progress', value: data.extended.progressAgainstPlan.overall, color: COLORS.onTarget },
          { name: 'Remaining', value: Math.max(0, 100 - data.extended.progressAgainstPlan.overall), color: COLORS.nearingTarget },
        ]
        isPercentage = true
      } else {
        chartData = []
      }
      break
    case 'specificKpis':
      if (data.extended?.specificKpis) {
        chartData = Object.entries(data.extended.specificKpis).map(([index, kpi]) => ({
          name: kpi.label,
          value: kpi.value,
          color: COLORS.onTarget
        }))
      } else {
        chartData = []
      }
      break
    case 'contractorComparison':
      if (data.extended?.contractorBreakdown && data.extended.contractorBreakdown.length > 0) {
        chartData = data.extended.contractorBreakdown.map((contractor, index) => ({
          name: useAcronyms
            ? (contractor.contractorAcronym || contractor.contractorName)
            : contractor.contractorName,
          fullName: contractor.contractorName,
          acronym: contractor.contractorAcronym || '',
          value: contractor.satisfactionPercentage || contractor.complaintsCount || contractor.progressPercentage || 0,
          color: [COLORS.onTarget, COLORS.nearingTarget, COLORS.belowTarget][index % 3]
        }))
        isPercentage = true
      } else {
        chartData = []
      }
      break
    case 'trendAnalysis':
      if (data.extended?.satisfactionPercentage?.trend12Months) {
        chartData = data.extended.satisfactionPercentage.trend12Months.map((value, index) => ({
          name: data.extended?.satisfactionPercentage?.periods[index] || `Period ${index + 1}`,
          value,
          period: data.extended?.satisfactionPercentage?.periods[index]
        }))
        isPercentage = true
      } else {
        chartData = []
      }
      break
    case 'submissionTimeliness':
      if (data.extended?.submissionTimeliness && data.extended.submissionTimeliness.length > 0) {
        chartData = data.extended.submissionTimeliness.map(contractor => {
          const days = contractor.averageDaysLate;
          let color: string;
          if (days <= 0) {
            color = COLORS.onTarget; // green: early or on time
          } else if (days <= 5) {
            color = COLORS.nearingTarget; // amber: slightly late
          } else {
            color = COLORS.belowTarget; // red: very late
          }
          return {
            name: useAcronyms
              ? (contractor.contractorAcronym || contractor.contractorName)
              : contractor.contractorName,
            fullName: contractor.contractorName,
            acronym: contractor.contractorAcronym || '',
            value: Math.round(days * 10) / 10,
            color
          }
        })
      } else {
        chartData = []
      }
      break
    default:
      chartData = []
  }

  // Filter out zero values for better chart display (skip for submissionTimeliness where negative/zero values are meaningful)
  const filteredChartData = chartType === 'submissionTimeliness'
    ? chartData
    : chartData.filter(item => item.value > 0)

  // For polar area, transform data for RadialBarChart - each item becomes a separate data point
  const polarData = filteredChartData.map(item => ({
    name: item.name,
    value: item.value,
    fill: item.color
  }));

  // Custom legend for polar area chart - defined inside component to access filteredChartData
  const PolarLegend = () => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 0 }}>
      {filteredChartData.map(item => (
        <span key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            display: 'inline-block',
            width: 16,
            height: 16,
            backgroundColor: item.color,
            borderRadius: 3,
            marginRight: 6,
          }} />
          <span style={{ color: item.color, fontWeight: 500 }}>{item.name}</span>
        </span>
      ))}
    </div>
  );

  // Table column headers
  const tableHeader1 = (chartType === 'contractorComparison' || chartType === 'submissionTimeliness') ? 'Contractor' : 'Category'
  const tableHeader2 = chartType === 'submissionTimeliness' ? 'Avg Days Late' : (isPercentage ? 'Percentage' : 'Count')

  // Format table cell value
  const formatTableValue = (value: number) => {
    if (chartType === 'submissionTimeliness') {
      const prefix = value > 0 ? '+' : ''
      return `${prefix}${value.toFixed(1)} days`
    }
    // #37: Count values are averages and were rendered as long floats
    // (e.g. 99.88888888888889). Round to a whole number for the table.
    return isPercentage ? `${value.toFixed(1)}%` : String(Math.round(value))
  }

  // Table JSX
  const tableJSX = (
    <TableContainer $sideBySide={tablePosition === 'left' || tablePosition === 'right'}>
      <StyledTable>
        <thead>
          <tr>
            <th>{tableHeader1}</th>
            <th>{tableHeader2}</th>
          </tr>
        </thead>
        <tbody>
          {chartData.map((item, index) => (
            <tr key={index}>
              <td>
                <span
                  style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    backgroundColor: item.color,
                    marginRight: '8px',
                    borderRadius: '50%'
                  }}
                />
                {item.name}
              </td>
              <td>
                {formatTableValue(item.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </TableContainer>
  )

  // Determine chartHeight based on tablePosition
  const chartHeight = (tablePosition === 'left' || tablePosition === 'right') ? 420 : height;

  return (
    <ChartContainer>
      <KpiFontGlobal />
      <NoChartOutline />
      {showTitle && (
        <SectionTitle title={title || chartTypeLabels[chartType]} size={SectionTitleSize.Medium} style={SectionTitleStyle.Dark} />
      )}
      {(tablePosition === 'left' || tablePosition === 'right') ? (
        <SideBySideContainer>
          {tablePosition === 'left' && tableJSX}
          <NoOutlineWrapper>
            <ChartWrapper height={chartHeight} $sideBySide={false}>
              {filteredChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  {chartDisplayType === 'pie' ? (
                    <PieChart>
                      <Pie
                        data={filteredChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={chartHeight / 3}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        isAnimationActive={true}
                        animationDuration={900}
                        activeIndex={activeIndex}
                        activeShape={undefined}
                        onMouseLeave={() => setActiveIndex(-1)}
                      >
                        {filteredChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            onMouseEnter={() => setActiveIndex(index)}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val: number, name: string) =>
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        }
                      />
                      {showLegend && (
                        <Legend
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{ margin: 0, padding: 0 }}
                        />
                      )}
                    </PieChart>
                  ) : chartDisplayType === 'doughnut' ? (
                    <PieChart>
                      <Pie
                        data={filteredChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={chartHeight / 3}
                        innerRadius={chartHeight / 6}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        isAnimationActive={true}
                        animationDuration={900}
                        activeIndex={activeIndex}
                        activeShape={undefined}
                        onMouseLeave={() => setActiveIndex(-1)}
                      >
                        {filteredChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            onMouseEnter={() => setActiveIndex(index)}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val: number, name: string) =>
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        }
                      />
                      {showLegend && (
                        <Legend
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{ margin: 0, padding: 0 }}
                        />
                      )}
                    </PieChart>
                  ) : chartDisplayType === 'bar' ? (
                    <BarChart data={filteredChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} width={chartHeight} height={chartHeight}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" hide={!showAxisLabels} />
                      <YAxis />
                      <Tooltip
                        formatter={(val: number, name: string) =>
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        }
                      />
                      {showLegend && (
                        <Legend
                          payload={filteredChartData.map(item => ({ value: item.name, type: 'square', color: item.color, id: item.name }))}
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{ margin: 0, padding: 0 }}
                        />
                      )}
                      <Bar dataKey="value" isAnimationActive={true} animationDuration={900}>
                        {filteredChartData.map((entry, index) => (
                          <Cell key={`bar-cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  ) : chartDisplayType === 'polar' ? (
                    filteredChartData.length > 0 ? (
                      <RadialBarChart
                        data={polarData}
                        innerRadius="20%"
                        outerRadius="90%"
                        startAngle={90}
                        endAngle={-270}
                        barSize={30}
                      >
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar
                          dataKey="value"
                          background
                          cornerRadius={10}
                          isAnimationActive={true}
                          animationDuration={900}
                          label={false}
                        >
                          {polarData.map((entry, index) => (
                            <Cell key={`polar-cell-${index}`} fill={entry.fill} />
                          ))}
                        </RadialBar>
                        <Tooltip
                          formatter={(val: number, name: string) =>
                            isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                          }
                        />
                        {showLegend && <Legend content={<PolarLegend />} />}
                      </RadialBarChart>
                    ) : <div></div>
                  ) : chartDisplayType === 'line' ? (
                    <LineChart data={filteredChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" hide={!showAxisLabels} />
                      <YAxis />
                      <Tooltip
                        formatter={(val: number, name: string) =>
                          isPercentage ? [`${val.toFixed(1)}%`, name] : [`${val}`, name]
                        }
                      />
                      {showLegend && <Legend />}
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={COLORS.onTarget}
                        strokeWidth={3}
                        dot={{ fill: COLORS.onTarget, strokeWidth: 2, r: 4 }}
                        isAnimationActive={true}
                        animationDuration={900}
                      />
                    </LineChart>
                  ) : <div>Unsupported chart type</div>}
                </ResponsiveContainer>
              ) : (
                <p>No {chartTypeLabels[chartType]} data available.</p>
              )}
            </ChartWrapper>
          </NoOutlineWrapper>
          {tablePosition === 'right' && tableJSX}
        </SideBySideContainer>
      ) : (
        <React.Fragment>
          <NoOutlineWrapper>
            <ChartWrapper height={chartHeight}>
              {filteredChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  {chartDisplayType === 'pie' ? (
                    <PieChart>
                      <Pie
                        data={filteredChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={chartHeight / 3}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        isAnimationActive={true}
                        animationDuration={900}
                        activeIndex={activeIndex}
                        activeShape={undefined}
                        onMouseLeave={() => setActiveIndex(-1)}
                      >
                        {filteredChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            onMouseEnter={() => setActiveIndex(index)}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val: number, name: string) =>
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        }
                      />
                      {showLegend && (
                        <Legend
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{ margin: 0, padding: 0 }}
                        />
                      )}
                    </PieChart>
                  ) : chartDisplayType === 'doughnut' ? (
                    <PieChart>
                      <Pie
                        data={filteredChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={chartHeight / 3}
                        innerRadius={chartHeight / 6}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        isAnimationActive={true}
                        animationDuration={900}
                        activeIndex={activeIndex}
                        activeShape={undefined}
                        onMouseLeave={() => setActiveIndex(-1)}
                      >
                        {filteredChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            onMouseEnter={() => setActiveIndex(index)}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val: number, name: string) =>
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        }
                      />
                      {showLegend && (
                        <Legend
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{ margin: 0, padding: 0 }}
                        />
                      )}
                    </PieChart>
                  ) : chartDisplayType === 'bar' ? (
                    <BarChart data={filteredChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} width={chartHeight} height={chartHeight}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" hide={!showAxisLabels} />
                      <YAxis />
                      <Tooltip
                        formatter={(val: number, name: string) =>
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        }
                      />
                      {showLegend && (
                        <Legend
                          payload={filteredChartData.map(item => ({ value: item.name, type: 'square', color: item.color, id: item.name }))}
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{ margin: 0, padding: 0 }}
                        />
                      )}
                      <Bar dataKey="value" isAnimationActive={true} animationDuration={900}>
                        {filteredChartData.map((entry, index) => (
                          <Cell key={`bar-cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  ) : chartDisplayType === 'polar' ? (
                    filteredChartData.length > 0 ? (
                      <RadialBarChart
                        data={polarData}
                        innerRadius="20%"
                        outerRadius="90%"
                        startAngle={90}
                        endAngle={-270}
                        barSize={30}
                      >
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar
                          dataKey="value"
                          background
                          cornerRadius={10}
                          isAnimationActive={true}
                          animationDuration={900}
                          label={false}
                        >
                          {polarData.map((entry, index) => (
                            <Cell key={`polar-cell-${index}`} fill={entry.fill} />
                          ))}
                        </RadialBar>
                        <Tooltip
                          formatter={(val: number, name: string) =>
                            isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                          }
                        />
                        {showLegend && <Legend content={<PolarLegend />} />}
                      </RadialBarChart>
                    ) : <div></div>
                  ) : chartDisplayType === 'line' ? (
                    <LineChart data={filteredChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" hide={!showAxisLabels} />
                      <YAxis />
                      <Tooltip
                        formatter={(val: number, name: string) =>
                          isPercentage ? [`${val.toFixed(1)}%`, name] : [`${val}`, name]
                        }
                      />
                      {showLegend && <Legend />}
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={COLORS.onTarget}
                        strokeWidth={3}
                        dot={{ fill: COLORS.onTarget, strokeWidth: 2, r: 4 }}
                        isAnimationActive={true}
                        animationDuration={900}
                      />
                    </LineChart>
                  ) : <div>Unsupported chart type</div>}
                </ResponsiveContainer>
              ) : (
                <p>No {chartTypeLabels[chartType]} data available.</p>
              )}
            </ChartWrapper>
          </NoOutlineWrapper>
          {tablePosition === 'below' && tableJSX}
        </React.Fragment>
      )}
    </ChartContainer>
  )
} 